import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { aboutInfo } from '@/mocks/mangaData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '发送失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <Layout>
      <div className="pt-20 pb-14 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-900 mb-3">
                关于{aboutInfo.brandName}
              </h1>
              <p className="text-lg text-foreground-500">{aboutInfo.tagline}</p>
            </div>

            <div className="max-w-3xl mx-auto">
              <p className="text-base text-foreground-700 leading-relaxed text-center">
                {aboutInfo.introduction}
              </p>
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="text-3xl md:text-4xl font-bold font-heading text-primary-600 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              为什么选择我们
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.features.map((feature) => (
                <div key={feature.title} className="bg-background-50 rounded-xl border border-background-200/70 p-5 md:p-6">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                    <i className="ri-check-double-line text-primary-600 text-lg"></i>
                  </div>
                  <h3 className="text-base font-bold font-heading text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-background-100 rounded-xl p-6 md:p-8">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-6">联系方式</h3>
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-weibo-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">微博</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">地址</p>
                      <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                <h3 className="text-lg font-bold font-heading text-foreground-900 mb-6">给我们留言</h3>
                <form onSubmit={handleSubmit} data-readdy-form="" action="https://readdy.ai/api/form/d9qr6t7nahb7382ove1g" method="POST">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">留言已发送，感谢您的反馈！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError || '发送失败，请稍后重试'}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}